import { useTranslation } from 'react-i18next';
import { ArrowRight, ChevronDown, ChevronRight, ChevronUp } from 'lucide-react';
import { cn } from '@utils/cn';
import { dayTimeLabel } from '../../helpers';
import { RelatedDecision } from '../../types';
import { RelatedCardViewProps } from './types';

const DECISIONS: RelatedDecision[] = ['move', 'keep', 'remove'];

const segmentClass = (value: RelatedDecision, active: boolean) =>
  cn(
    'h-9 rounded-md border px-3.5 text-[15px] disabled:opacity-60',
    !active && 'border-transparent bg-neutral-50 text-neutral-500',
    active && value === 'remove' && 'border-danger-200 bg-danger-50 font-semibold text-danger-600',
    active &&
      value !== 'remove' &&
      'border-primary-100 bg-primary-50 font-semibold text-primary-600',
  );

export const RelatedCardView = ({
  name,
  icon,
  owner,
  from,
  to,
  decision,
  expanded,
  result,
  error,
  updated,
  disabled,
  onDecision,
  onToggle,
  onEdit,
  confirmed,
  children,
}: RelatedCardViewProps) => {
  const { t } = useTranslation('app', { keyPrefix: 'scheduling.phone' });
  const removing = decision === 'remove';
  const moving = decision === 'move' && !result;
  const chip = result === 'removed' ? 'removed' : result === 'moved' || updated ? 'updated' : '';

  return (
    <div
      className={cn(
        'overflow-hidden rounded-xl border-[1.5px] bg-white',
        error ? 'border-danger-300' : 'border-neutral-200',
      )}>
      <div className="flex gap-3 p-3">
        <div
          className={cn(
            'flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-lg font-bold',
            removing ? 'bg-neutral-50 text-neutral-400' : 'bg-cyan-50 text-cyan-700',
          )}>
          {icon}
        </div>
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <div className="flex items-start gap-2">
            <h3
              className={cn(
                'min-w-0 flex-1 text-[17px] font-bold leading-tight',
                removing ? 'text-neutral-400' : 'text-neutral-800',
              )}>
              <span className="mr-2">{name}</span>
              {chip && (
                <span
                  className={cn(
                    'inline-flex h-[21px] items-center rounded-full px-2 align-middle text-[11px] font-bold',
                    chip === 'removed'
                      ? 'bg-danger-50 text-danger-600'
                      : 'bg-success-100 text-success-800',
                  )}>
                  {t(`related.${chip}`)}
                </span>
              )}
            </h3>
            {moving && (
              <button
                type="button"
                aria-expanded={expanded}
                aria-label={t(expanded ? 'collapse' : 'expand')}
                onClick={onToggle}
                className="-mr-1 -mt-1 flex h-8 w-8 shrink-0 items-center justify-center text-neutral-600">
                {expanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
              </button>
            )}
          </div>
          {owner && <p className="truncate text-[15px] text-neutral-600">{owner}</p>}
          <p className="flex flex-wrap items-center gap-x-1.5 text-sm">
            {decision === 'keep' || (result === 'moved' && !to) ? (
              <span className="text-neutral-600">{dayTimeLabel(from)}</span>
            ) : (
              <>
                <span className="text-neutral-400 line-through">{dayTimeLabel(from)}</span>
                {removing ? (
                  <span className="font-semibold text-danger-600">{t('related.removed')}</span>
                ) : (
                  to && (
                    <>
                      <ArrowRight className="h-3.5 w-3.5 text-neutral-500" />
                      <span className="font-semibold text-primary-600">{dayTimeLabel(to)}</span>
                    </>
                  )
                )}
              </>
            )}
          </p>
          {!result && (
            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              {DECISIONS.map((value) => (
                <button
                  key={value}
                  type="button"
                  aria-pressed={decision === value}
                  disabled={disabled}
                  onClick={() => onDecision(value)}
                  className={segmentClass(value, decision === value)}>
                  {t(value === 'keep' ? 'keep' : `related.${value}`)}
                </button>
              ))}
              {!removing && (
                <button
                  type="button"
                  disabled={disabled}
                  onClick={onEdit}
                  className="ml-auto flex h-9 items-center gap-0.5 text-[15px] font-semibold text-info-600 disabled:opacity-60">
                  {t('related.editDetails')}
                  <ChevronRight className="h-4 w-4" />
                </button>
              )}
            </div>
          )}
          {error && <p className="mt-2 text-sm font-medium text-danger-600">{error}</p>}
        </div>
      </div>
      {moving && expanded && (
        <div className="flex flex-col gap-4 border-t border-neutral-100 px-3 py-4">{children}</div>
      )}
      {moving && <div className="border-t border-neutral-100 px-3 py-3">{confirmed}</div>}
    </div>
  );
};
